/* One trip (2026-10-28): the classic bus page's run map and "pin this run", in
   the console. Opened from a bus's Last runs, or from Analytics → Pinned trips
   (?bus=…&trip=…). The whole record is read here, trail and all — the fleet
   load leaves trails behind (firestoreRest) and this is the one place that
   draws one. A pinned trip is listed under Analytics → Pinned trips by the
   name given here; the rules let the school's fleet admin write it. */
import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { ConsoleModal, fieldInput, ModalField } from './Modal';
import { TripMap } from './RouteMap';
import { useShell } from './Shell';
import { dayMonth, hhmm, n0, plural, useToast } from './kit';
import { loadRun, pinRun, sideLabel, TrailPoint } from './data/analytics';
import { sideOfRouteType } from '../utils/routeWindow';
import type { Bus, FleetModel } from './data/model';

type Raw = Record<string, unknown>;
const when = (v: unknown): Date | null => (v && typeof (v as { toDate?: () => Date }).toDate === 'function' ? (v as { toDate: () => Date }).toDate() : null);
const str = (v: unknown) => (typeof v === 'string' ? v : '');

export const TripModal: React.FC<{
  f: FleetModel; bus: Bus; runId: string; onClose: () => void;
  /** After a pin or unpin, so the list behind can show it before the fleet refreshes. */
  onPinned?: (name: string | null) => void;
}> = ({ f, bus, runId, onClose, onPinned }) => {
  const { dark } = useShell();
  const { toast } = useToast();
  const [run, setRun] = useState<{ data: Raw; path: TrailPoint[] } | null | 'gone'>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setRun(null);
    loadRun(runId)
      .then((r) => { if (live) setRun(r || 'gone'); })
      .catch(() => { if (live) setRun('gone'); });
    return () => { live = false; };
  }, [runId]);

  const data = run && run !== 'gone' ? run.data : null;
  const started = when(data?.routeStartedAt) || when(data?.date);
  const finished = when(data?.routeFinishedAt);
  const side = sideLabel(sideOfRouteType(str(data?.routeType) || null));
  const kids = Array.isArray(data?.children) ? (data?.children as Raw[]) : [];
  const served = kids.filter((c) => ['picked_up', 'dropped_off', 'dropped'].includes(str(c.status))).length;
  const km = typeof data?.distanceKm === 'number' ? data.distanceKm : null;
  const minutes = typeof data?.durationMinutes === 'number' ? data.durationMinutes
    : started && finished ? Math.round((finished.getTime() - started.getTime()) / 60000) : null;
  const driver = f.drivers.find((p) => p.id === str(data?.driverId))?.name || null;
  const pinned = data?.pinned === true ? (str(data.pinnedName) || 'Pinned trip') : null;
  const suggested = `${side}${started ? ` · ${dayMonth(started)}` : ''}`;

  useEffect(() => { if (data && !pinned) setName(suggested); }, [data, pinned, suggested]);

  const pin = (to: string | null) => {
    if (busy || !data) return;
    setBusy(true);
    pinRun(runId, to, auth.currentUser?.uid || null)
      .then(() => {
        setRun((r) => (r && r !== 'gone' ? { ...r, data: { ...r.data, pinned: !!to, pinnedName: to } } : r));
        onPinned?.(to);
        toast({ kind: 'success', text: to ? `Pinned as “${to}”. Find it under Analytics → Pinned trips.` : 'Unpinned. It stays in this bus’s runs.' });
      })
      .catch(() => toast({ kind: 'error', text: 'Couldn’t save the pin. Try again in a minute.' }))
      .finally(() => setBusy(false));
  };

  const facts: [string, string][] = data ? [
    ['When', started ? `${started.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · ${hhmm(started)}${finished ? `–${hhmm(finished)}` : ''}` : '—'],
    ['Distance', km != null && km > 0 ? `${km.toFixed(1)} km` : '—'],
    ['Time', minutes != null && minutes > 0 ? `${n0(minutes)} min` : '—'],
    ['Children', kids.length ? `${served} of ${plural(kids.length, 'child', 'children')}` : '—'],
    ['Driver', driver || '—'],
  ] : [];

  return (
    <ConsoleModal title={`${bus.name} · ${side.toLowerCase()} trip`} onClose={onClose} width={760}
      onSubmit={pinned ? undefined : () => pin(name.trim() || suggested)} enterHint={pinned ? undefined : 'pin'}>
      {run === 'gone' ? (
        <p style={{ margin: 0 }}>That trip is no longer on file.</p>
      ) : (
        <>
          <div style={{ height: 320, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)' }}>
            <TripMap path={run ? run.path : null} dark={dark} />
          </div>
          <div className="kv" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
            {(data ? facts : [['When', '…']]).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="sm" style={{ color: 'var(--muted)' }}>{k}</span>
                <b style={{ fontWeight: 600 }}>{v}</b>
              </div>
            ))}
          </div>
          {data && (pinned ? (
            <div className="row" style={{ gap: 10, flexWrap: 'wrap', borderTop: '1px solid var(--line)', paddingTop: 14 }}>
              <span style={{ flex: 1 }}>Pinned as <b>{pinned}</b>. It is listed under Analytics → Pinned trips.</span>
              <button type="button" className="btn s b-line" onClick={() => pin(null)} disabled={busy}>Unpin</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid var(--line)', paddingTop: 14 }}>
              <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex' }}>
                <ModalField label="Pin it to find it later" hint="Pinned trips are listed under Analytics, by this name.">
                  {(id, ring) => <input id={id} {...ring} style={fieldInput} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />}
                </ModalField>
              </div>
              <button type="button" className="btn b-ink" onClick={() => pin(name.trim() || suggested)} disabled={busy} style={{ marginBottom: 22 }}>Pin this trip</button>
            </div>
          ))}
        </>
      )}
    </ConsoleModal>
  );
};
